import { gql, gqlFetch } from '@/service/gql'
import { signedIn } from '@/auth/authRef'
import { bindBrowserLocale, setLocale } from '@amzn/amp-i18n'
import { errorMessage } from '@/util/errorUtil'
import { logWarn, type Unsubscribe } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { getCacheManager } from '@/core/offlineCacheManager'
import { reloadApplication } from '@/core/reloadApplication'
import { clearNormalizedCache } from '@amzn/amp-gql'

/**
 * UI display-language preference (the account-wide primary language).
 *
 * Mirrors GH's ChangeLanguageModal: the saved preference is the authoritative
 * display locale and the browser/device locale is only a fallback. GH lost the
 * choice on reload because boot always bound the app to the browser locale and
 * nothing re-read the saved preference. We cache the chosen `LocaleCode`
 * locally so it seeds the locale synchronously on the next boot (surviving
 * reload without a round-trip) and, when signed in, reconcile it from the
 * server `userPreferences.primaryLanguage` so the account choice follows the
 * customer across devices (same shape as `explicitLanguageFilter`).
 *
 * The reconcile is overlay-authoritative-within-TTL, the same model the
 * amp-gql like/unlike overlay uses: a just-made local choice out-ranks any
 * server read for a short window. Without it the gateway's read-after-write
 * cache echoed the pre-change `primaryLanguage`, so the sign-in reconcile — on
 * a fresh boot, or landing mid-change on the first change of a session —
 * reverted the choice both in memory and in the local cache. The window guards
 * the choice until the write propagates; cross-device sync resumes once it
 * lapses.
 */

const { target, errorHandler } = ctx('displayLanguageService')

/** localStorage key for the chosen UI language (a wire `LocaleCode`, e.g. `de_DE`). */
const DISPLAY_LANGUAGE_KEY = 'displayLanguage'

/**
 * The wire `LocaleCode`s the app ships UI translations for — the flat union of
 * every territory's offered languages (see `DisplayLanguageModal`). A change
 * target outside this set has no bundle and would fall back to the source
 * locale, so it's the validation set for the agent's language action.
 */
export const SUPPORTED_DISPLAY_LANGUAGES = [
    'en_US',
    'es_US',
    'fr_CA',
    'de_DE',
    'it_IT',
    'ja_JP',
    'pt_BR',
    'en_CA',
    'es_MX',
    'en_GB',
    'es_ES',
    'fr_FR',
    'en_IN',
    'en_AU',
] as const

/** A wire `LocaleCode` the app ships UI translations for. */
export type SupportedDisplayLanguage = (typeof SUPPORTED_DISPLAY_LANGUAGES)[number]

/** localStorage key for the local-choice overlay deadline (epoch ms). */
const OVERLAY_UNTIL_KEY = 'displayLanguageOverlayUntil'

// A local choice out-ranks a server read for this window. Matches the amp-gql
// optimistic-overlay ceiling (MAX_OPTIMISTIC_UPDATE_TIME_SECONDS) so both cover
// the same gateway read-after-write cache window.
const OVERLAY_TTL_SECONDS = 600

const updatePrimaryLanguageMutation = gql(`
    mutation updatePrimaryLanguage($primaryLanguage: String) {
        updatePrimaryLanguage(primaryLanguage: $primaryLanguage) {
            primaryLanguage {
                id
            }
        }
    }
`)

const getPrimaryLanguageQuery = gql(`
    query getPrimaryLanguage {
        userPreferences {
            primaryLanguage {
                id
            }
        }
    }
`)

const toBcp47 = (code: string) => code.replace('_', '-')

let browserLocaleSub: Unsubscribe | null = null

function readLocalDisplayLanguage(): string | undefined {
    try {
        return localStorage.getItem(DISPLAY_LANGUAGE_KEY) ?? undefined
    } catch {
        return undefined
    }
}

function writeLocalDisplayLanguage(code: string): void {
    try {
        localStorage.setItem(DISPLAY_LANGUAGE_KEY, code)
    } catch {
        /* private-mode Safari — ignore. */
    }
}

function writeOverlayUntil(epochMs: number): void {
    try {
        if (epochMs > 0) {
            localStorage.setItem(OVERLAY_UNTIL_KEY, String(epochMs))
        } else {
            localStorage.removeItem(OVERLAY_UNTIL_KEY)
        }
    } catch {
        /* private-mode Safari — ignore. */
    }
}

/** Whether a just-made local choice still out-ranks a server read. */
function localOverlayActive(): boolean {
    try {
        const until = Number(localStorage.getItem(OVERLAY_UNTIL_KEY))
        return Number.isFinite(until) && Date.now() < until
    } catch {
        return false
    }
}

function stopBrowserTracking(): void {
    browserLocaleSub?.()
    browserLocaleSub = null
}

/** The locally-cached display `LocaleCode`, or undefined when none is set. */
export function currentDisplayLanguage(): string | undefined {
    return readLocalDisplayLanguage()
}

/**
 * Boot seed for the app UI locale. An explicitly chosen display language wins
 * over the browser locale; with no saved choice, bind to the browser locale
 * and keep tracking `languagechange`.
 */
export function initializeDisplayLocale(): void {
    const saved = readLocalDisplayLanguage()
    if (saved) {
        setLocale(toBcp47(saved))
        return
    }
    browserLocaleSub = bindBrowserLocale()
}

/**
 * Apply + persist a display-language `LocaleCode` the customer just chose: stop
 * tracking the browser locale (the explicit choice outranks it), cache it so it
 * survives reload, stamp the overlay window so a stale server read can't revert
 * it, and flip the active locale so every bound `getString` re-emits.
 */
export function applyDisplayLanguage(code: string): void {
    stopBrowserTracking()
    writeLocalDisplayLanguage(code)
    writeOverlayUntil(Date.now() + OVERLAY_TTL_SECONDS * 1000)
    setLocale(toBcp47(code))
}

/**
 * The full language-change operation, shared by the header picker and the agent
 * action so both take one code path: persist the choice locally + flip the
 * active locale (every service request then carries it as Content-Language),
 * write it to the account for cross-device sync, then drop the old-locale
 * responses from the runtime cache and the normalized entity cache, and
 * re-mount the app so server-driven (firefly) strings come back localized.
 *
 * The entity cache clear matters because this is a soft remount, not GH's hard
 * `location.reload` — the in-memory entity store would otherwise survive it,
 * and a fresh query's scalars merge over (rather than replace) a cached
 * entity, letting stale-locale strings persist on it after the switch.
 *
 * Order matters: the local persist + `setLocale` run BEFORE the cache clears and
 * reload, so the re-mounted app boots in the new locale (the choice is cached in
 * localStorage, surviving the reload) and refetches localized data. The account
 * write is best-effort — a signed-out / offline failure must not block the
 * local change or the refresh.
 */
export async function applyDisplayLanguageAndRefresh(code: string): Promise<void> {
    applyDisplayLanguage(code)
    try {
        const confirmed = await setPrimaryLanguage(code)
        if (confirmed && confirmed !== code) applyDisplayLanguage(confirmed)
    } catch (e) {
        logWarn(target, 'primaryLanguage persist failed; refreshing anyway', errorMessage(e))
    }
    await getCacheManager().clear()
    clearNormalizedCache()
    reloadApplication()
}

/**
 * Reconcile the display locale from a server-resolved `LocaleCode`. Unlike the
 * local choice this does NOT open an overlay window — the server value is
 * already authoritative, so a subsequent reconcile may freely replace it.
 */
function setDisplayLanguageFromServer(code: string): void {
    stopBrowserTracking()
    writeLocalDisplayLanguage(code)
    writeOverlayUntil(0)
    setLocale(toBcp47(code))
}

/**
 * Persists the customer's UI display language and returns the server-confirmed
 * `LocaleCode` so the caller can reconcile against the value the backend stored.
 */
export async function setPrimaryLanguage(primaryLanguage: string): Promise<string | undefined> {
    try {
        const result = await gqlFetch(updatePrimaryLanguageMutation, { primaryLanguage })
        return result.updatePrimaryLanguage?.primaryLanguage?.id ?? undefined
    } catch (e) {
        logWarn(target, 'setPrimaryLanguage failed', errorMessage(e))
        throw e
    }
}

async function getPrimaryLanguage(): Promise<string | undefined> {
    try {
        const result = await gqlFetch(getPrimaryLanguageQuery, {})
        return result.userPreferences?.primaryLanguage?.id ?? undefined
    } catch (e) {
        logWarn(target, 'getPrimaryLanguage failed', errorMessage(e))
        return undefined
    }
}

/**
 * Reconcile the display locale from the server whenever the customer becomes
 * signed in: the account's saved `primaryLanguage` is authoritative across
 * devices, so it overrides the local seed / browser locale — UNLESS a local
 * choice is still within its overlay window, in which case the (possibly stale,
 * read-after-write) server value is ignored so it can't revert a just-made
 * change. Returns a disposer.
 */
export function installDisplayLanguageSync(): Unsubscribe {
    return signedIn().onData((isSignedIn) => {
        if (!isSignedIn) return
        getPrimaryLanguage()
            .then((code) => {
                if (code && !localOverlayActive()) setDisplayLanguageFromServer(code)
            })
            .catch(errorHandler)
    })
}
